import { z } from "zod";
import React, { ReactNode } from "react";

import type { PropertyUI } from "@jitsu/core-functions/src/lib/meta-types";
import { ConnectionMappings } from "@jitsu/mapping-engine/schemas";

// Connection filter schemas (duplicated from @jitsu/core-functions to avoid server-side dependencies)
const MAX_FILTERS_PER_CONNECTION = 10;

const FilterCondition = z.object({
  field: z.enum(["type", "event", "properties"]),
  path: z.string().optional(),
  operator: z.enum(["equals", "not_equals", "contains", "not_contains", "exists", "not_exists"]),
  value: z.union([z.string(), z.number(), z.boolean()]).optional(),
});

const FilterRule = z.object({
  id: z.string(),
  name: z.string().optional(),
  enabled: z.boolean().default(true),
  conditions: z.array(FilterCondition).min(1),
  action: z.enum(["drop", "allow"]),
});

const ConnectionFilters = z.object({
  filters: z.array(FilterRule).max(MAX_FILTERS_PER_CONNECTION).default([]),
});

import amplitudeIcon from "./icons/amplitude";
import bigqueryIcon from "./icons/bigquery";
import ClickhouseIcon from "./icons/clickhouse";
import devnullIcon from "./icons/devnull";
import gcsIcon from "./icons/gcs";
import hubspotIcon from "./icons/hubspot";
import mixpanelIcon from "./icons/mixpanel";
import facebookIcon from "./icons/facebook";
import juneIcon from "./icons/june";
import brazeIcon from "./icons/braze";
import salesforceIcon from "./icons/salesforce";
import mongodbIcon from "./icons/mongodb";
import klaviyoIcon from "./icons/klaviyo";
import attentiveIcon from "./icons/attentive";
import tikTokIcon from "./icons/tiktok";
import snapchatIcon from "./icons/snapchat";
import spotifyIcon from "./icons/spotify";
import ga4Icon from "./icons/ga4";
import gtmIcon from "./icons/gtm";
import googleAdsIcon from "./icons/googleAds";
import ninetailedIcon from "./icons/ninetailed";
import postgresIcon from "./icons/postgres";
import mysqlIcon from "./icons/mysql";
import motherduckIcon from "./icons/motherduck";
import redshiftIcon from "./icons/redshift";
import posthogIcon from "./icons/posthog";
import segmentIcon from "./icons/segment";
import s3Icon from "./icons/s3";
import tagIcon from "./icons/tag";
import pinterestIcon from "./icons/pinterest-tag";
import snowflakeIcon from "./icons/snowflake";
import logRocketIcon from "./icons/logrocket";
import intercomIcon from "./icons/intercom";
import webhookIcon from "./icons/webhook";
import bingIcon from "./icons/bing";
import openAiIcon from "./icons/openai";
import nostoIcon from "./icons/nosto";
import contentsquareIcon from "./icons/contentsquare";
import { ChordIconBlack } from "./icons/chord";
import impactIcon from "./icons/impact";
import iterableIcon from "./icons/iterable";
import delightedIcon from "./icons/delighted";
import { branding } from "../branding";
import awinPixelIcon from "./icons/awin-pixel";
import algoliaIcon from "./icons/algolia";
import twitterIcon from "./icons/twitter";
import tripleWhaleIcon from "./icons/triple-whale";
import axonIcon from "./icons/axon";
import northbeamIcon from "./icons/northbeam";
import northbeamPixelIcon from "./icons/northbeam-pixel";
import redditIcon from "./icons/reddit";
import dotdigitalIcon from "./icons/dotdigital";
import emotiveIcon from "./icons/emotive";
import stackAdaptIcon from "./icons/stackadapt";
import shopMyIcon from "./icons/shopmy";
import postieIcon from "./icons/postie";
import retentionIcon from "./icons/retention";
import roktIcon from "./icons/rokt";
import theTradeDeskIcon from "./icons/thetradedesk";
import yahooIcon from "./icons/yahoo";
import mntnIcon from "./icons/mntn";
import heapIcon from "./icons/heap";
import insiderOneIcon from "./icons/insider-one";
import statsigIcon from "./icons/statsig";
import amazonIcon from "./icons/amazon";
import * as meta from "@jitsu/core-functions/src/meta";

const s3Regions = [
  "us-west-1",
  "us-west-2",
  "us-east-2",
  "us-east-1",
  "ap-south-1",
  "ap-northeast-3",
  "ap-northeast-2",
  "ap-southeast-1",
  "ap-southeast-2",
  "ap-northeast-1",
  "ca-central-1",
  "cn-north-1",
  "cn-northwest-1",
  "eu-central-1",
  "eu-west-1",
  "eu-west-2",
  "eu-south-1",
  "eu-west-3",
  "eu-north-1",
  "me-south-1",
  "sa-east-1",
  "us-gov-east-1",
  "us-gov-west-1",
] as const;

export type SchemaUI = Record<string, PropertyUI>;

export const FunctionsConnectionOptions = z.object({
  functions: z.array(z.object({ functionId: z.string(), functionOptions: z.any().optional() })).optional(),
  functionsEnv: z.record(z.string(), z.string()).optional(),
  debugTill: z.string().optional(),
  filters: ConnectionFilters.optional(),
  mappings: ConnectionMappings.optional(),
  realtime: z.boolean().optional().default(false),
  // Per-connection timeout overrides (ms). When unset, rotor falls back to FETCH_TIMEOUT_MS / UDF_TIMEOUT_MS env vars.
  // fetchTimeoutMs must be lower than udfTimeoutMs to avoid a race condition at the isolated-vm boundary.
  fetchTimeoutMs: z.number().int().min(1000).max(60000).nullish(),
  udfTimeoutMs: z.number().int().min(1000).max(60000).nullish(),
  // Per-connection event deduplication window (minutes). When set, events delivered to this destination
  // within the window are tracked by messageId in Redis and duplicate deliveries are skipped.
  // When unset, no deduplication is performed (default behavior).
  eventDedupeWindowMinutes: z.number().int().min(5).max(60).nullish(),
  // Per-connection retry configuration. When unset, rotor uses the global defaults from
  // MESSAGES_RETRY_COUNT (default 3) and MESSAGES_RETRY_BACKOFF_BASE (default 10 minutes).
  // Values are clamped at runtime to operator floor/ceiling env vars regardless of what is stored here.
  // The schema intentionally uses wide bounds so manually-adjusted DB values don't prevent opening
  // the Connection Editor; the UI enforces narrower limits via InputNumber, and rotor clamps at runtime.
  retryCount: z.number().int().min(0).nullish(),
  retryBackoffBase: z.number().int().min(5).nullish(), // minutes; floored at Bulker's retry poll interval
});

//Options of any source -> destination connection that are not specific to any particular destination
export const CloudDestinationsConnectionOptions = z
  .object({
    multithreading: z.boolean().optional(),
  })
  .merge(FunctionsConnectionOptions);
export type CloudDestinationsConnectionOptions = z.infer<typeof CloudDestinationsConnectionOptions>;

//Auxiliary type for batch mode options
export const BatchModeOptions = z.object({
  batchSize: z.number().min(1).default(10000),
  frequency: z
    .number()
    .int()
    .min(1)
    .max(60 * 24)
    // default for the form only: zod 4 would apply .default(5).nullish() to a missing value, zod 3 never did
    .meta({ default: 5 })
    .nullish(),
});
export type BatchModeOptions = z.infer<typeof BatchModeOptions>;

/**
 * Common settings for device destination connections.
 * Note: Functions are not supported for device-mode destinations since they run
 * in the browser, not on the server.
 */
export const DeviceDestinationsConnectionOptions = z.object({
  events: z.string().optional().default("*"),
  hosts: z.string().optional().default("*"),
  mappings: ConnectionMappings.optional(),
});

export type DeviceDestinationsConnectionOptions = z.infer<typeof DeviceDestinationsConnectionOptions>;

//All possible options for Bulker based source -> destination connection
export const BaseBulkerConnectionOptions = z
  .object({
    mode: z.enum(["stream", "batch"]).default("batch"),
    primaryKey: z.string().default("message_id"),
    deduplicate: z.boolean().default(true),
    deduplicateWindow: z.number().default(31),
    timestampColumn: z.string().default("timestamp"),
    dataLayout: z.enum(["segment", "jitsu-legacy", "segment-single-table", "passthrough"]).default("segment"),
    schemaFreeze: z.boolean().default(false),
    keepOriginalNames: z.boolean().default(false),
  })
  .merge(BatchModeOptions)
  .merge(FunctionsConnectionOptions);

export type BaseBulkerConnectionOptions = z.infer<typeof BaseBulkerConnectionOptions>;

export const AllConnectionOptions = BaseBulkerConnectionOptions.merge(DeviceDestinationsConnectionOptions).merge(
  CloudDestinationsConnectionOptions
);

export type AllConnectionOptions = Partial<z.infer<typeof AllConnectionOptions>>;

/**
 * There's a little copy-paste between here and jitsu-js
 */
export type AnalyticsPluginDescriptor = {
  type: "analytics-plugin";
  //link to a CDN with the plugin source code
  packageCdn: string;
  //name of the variable where plugin object is exported
  moduleVarName: string;
};

export type InternalPluginDescriptor = {
  type: "internal-plugin";
  name: string;
};

export type DeviceOptions = AnalyticsPluginDescriptor | InternalPluginDescriptor;

export type DestinationType<T = any> = {
  id: string;
  title: string;
  isSynchronous?: boolean;
  usesBulker?: boolean;
  // destinations that relies on both rotor for flexible logic in typescript and bulker for batching
  hybrid?: boolean;
  tags: string | string[];
  credentials: z.ZodObject<z.ZodRawShape>;
  connectionOptions: z.ZodObject<z.ZodRawShape>;
  credentialsUi?: SchemaUI;
  comingSoon?: boolean;
  icon?: ReactNode;
  description: ReactNode;
  documentation?: ReactNode;
  //For cloud (=server side) destinations - name builtin of the function that implements it
  implementingFunction?: string;
  //For device destinations - how this destination should be invoked? Information such as analytics plugin name, package name
  //etc. Not typed yet since so far each destination has its own settings
  deviceOptions?: DeviceOptions;

  /*
   * If destination support sync from connector packages, here's a place to define it
   * key is a FQN of the connector package, li
   */
  syncs?: {
    [key: string]: {
      syncOptions: z.ZodObject<z.ZodRawShape>;
      description: ReactNode;
    };
  };
};

export const blockStorageSettings = z.object({
  folder: z.string().optional().describe("Folder in the block storage bucket where files will be stored"),
  format: z
    .enum(["ndjson", "ndjson_flat", "csv"])
    .default("ndjson")
    .describe(
      "Format of the files stored in the block storage: <b>ndjson</b> - Newline Delimited JSON, <b>ndjson_flat</b> - Newline Delimited JSON flattened, <b>csv</b> - CSV"
    ),
  compression: z
    .enum(["gzip", "none"])
    .default("gzip")
    .describe(
      "Compression mode used for the files stored in the block storage:<br/><b>gzip</b> - files will be compressed and have <code>.gz</code> filename suffix and <code>Content-Type: application/gzip</code><br/><b>none</b> - no compression, <code>Content-Type</code> and file extension will be set according to the format"
    ),
});

export function getCoreDestinationType(typeId: string): DestinationType {
  const destinationType = coreDestinationsMap[typeId];
  if (!destinationType) {
    throw new Error(
      `Destination type ${typeId} is not found. Available types: ${coreDestinations.map(d => d.id).join(", ")}`
    );
  }
  return destinationType;
}

export function getCoreDestinationTypeNonStrict(typeId: string): DestinationType | undefined {
  return coreDestinationsMap[typeId];
}

export const ClickhouseCredentials = z.object({
  protocol: z
    .enum(["http", "https", "clickhouse", "clickhouse-secure"])
    .default("clickhouse-secure")
    .describe(
      "Protocol used for ClickHouse connection: <code>http</code>, <code>https</code>, <code>clickhouse</code>, <code>clickhouse-secure</code>"
    ),
  hosts: z
    .array(z.string())
    .describe(
      "List of clickhouse servers as <code>host:port</code>. If port is not specified, default port for respective protocol will be used: <code>http->8123</code>, <code>https->8443</code>, <code>clickhouse->9000</code>, <code>clickhouse-secure->9440</code>"
    ),
  username: z.string().default("default").describe("Username for ClickHouse connection"),
  password: z.string().describe("Password for ClickHouse connection"),
  cluster: z
    .string()
    .optional()
    .describe("Name of cluster to use.<br/>For <b>ClickHouse Cloud</b> or single-node setups, leave this field empty."),
  database: z.string().default("default").describe("Name of the database to use"),
  parameters: z
    .object({})
    .catchall(z.string().default(""))
    .optional()
    .describe(
      "Additional parameters for ClickHouse driver. See <a href='https://clickhouse.com/docs/en/integrations/go#connection-settings-1' rel='noreferrer noopener' target='_blank'>Clickhouse documentation</a>"
    ),
  loadAsJson: z
    .boolean()
    .default(false)
    .describe(
      "Use JSONEachRow format::Load data in the <a href='https://clickhouse.com/docs/en/interfaces/formats' rel='noreferrer noopener' target='_blank'>JSONEachRow</a> format. This method offers better performance but may not work correctly on older ClickHouse versions."
    ),
});

export type ClickhouseCredentials = z.infer<typeof ClickhouseCredentials>;

const logRocketDestination = {
  id: "logrocket",
  isSynchronous: true,
  icon: logRocketIcon,
  tags: "Device Destinations",
  title: "Log Rocket",
  description: "Log Rocket is a session replay tool that helps you understand how users interact with your app.",
  credentials: z.object({
    //a json representation of the code - value & language. We can't use ordinary object here
    //because 3rd-party form renderer doesn't support it
    appId: z.string().describe("Log Rocket App ID. Go to Settings » Project Setup » General Settings to find it"),
  }),
  deviceOptions: {
    type: "internal-plugin",
    name: "logrocket",
  } as DeviceOptions,
  connectionOptions: DeviceDestinationsConnectionOptions,
};

const tagDestination = {
  id: "tag",
  isSynchronous: true,
  icon: tagIcon,
  title: "Tag",
  tags: "Device Destinations",
  description: "Inserts any html or javascript into your page. Use this to add any third party tracking code.",
  credentials: z.object({
    //a json representation of the code - value & language. We can't use ordinary object here
    //because 3rd-party form renderer doesn't support it
    code: z.string().describe("Code to insert into site page in <code>html</code> or <code>javascript</code> format"),
  }),
  deviceOptions: {
    type: "internal-plugin",
    name: "tag",
  } as DeviceOptions,
  credentialsUi: {
    code: {
      editor: "SnippedEditor",
      editorProps: { languages: ["html", "javascript"] },
    },
  },
  connectionOptions: DeviceDestinationsConnectionOptions,
};

const pinterestTagDestination = {
  id: "pinterest-tag",
  isSynchronous: true,
  icon: pinterestIcon,
  title: "Pinterest Tag",
  tags: "Device Destinations",
  description:
    "The Pinterest tag allows you to track actions people take on your website after viewing your Promoted Pin.",
  credentials: meta.PinterestTagCredentials,

  deviceOptions: {
    type: "internal-plugin",
    name: "pinterest-tag",
  } as DeviceOptions,
  connectionOptions: DeviceDestinationsConnectionOptions,
};

const gaDeviceDestination = {
  id: "ga4-tag",
  isSynchronous: true,
  icon: ga4Icon,
  title: "Google Analytics 4 (Device Mode)",
  tags: "Device Destinations",
  description: "Tracks users in Google Analytics with client side code snippet. ",
  credentials: z.object({
    measurementIds: z
      .string()
      .describe(
        "Measurement ID::Measurement ID of your Google Analytics 4 properties. <a href='https://support.google.com/analytics/answer/9539598?hl=en' target='_blank' rel='noreferrer noopener'>How to find</a>"
      ),
    autoPageView: z
      .boolean()
      .default(true)
      .describe(
        "Rely on <a href='https://support.google.com/analytics/answer/9216061#page_view' target='_blank' rel='noopener noreferrer'>Enhanced event measurement</a> to track page views. Chord <code>page</code> event will be ignored."
      ),
    debugMode: z.boolean().default(false).describe("Enable debug mode"),
    forwardExtraProperties: z
      .boolean()
      .default(false)
      .describe(
        "Forward extra event properties::When enabled, event properties not covered by the built-in GA4 mapping or your configured custom mappings are forwarded to GA4 automatically. Mapped properties always take precedence."
      ),
    dataLayerName: z
      .string()
      .optional()
      .describe(
        "Custom data layer name::Window-scoped array name for this GA4 install. Leave blank to use the standard <code>window.dataLayer</code> (recommended). Set to a custom name (e.g., <code>dataLayerGA</code>) to isolate this install from GTM or other GA4 plugins."
      ),
    customEventsAndParameters: z.array(
      z.object({
        chordEvent: z.string().describe("Event name to map to Google Analytics event"),
        googleEvent: z.string().describe("Google Analytics event name"),
        parameters: z.array(
          z.object({
            eventProperty: z.string().describe("Event property to map to Google Analytics parameter"),
            googleProperty: z.string().describe("Google Analytics parameter name"),
          })
        ),
      })
    ),
    adConsentCategory: z
      .string()
      .optional()
      .describe(
        "Ad Consent Category::The consent category key (from your CMP) that controls advertising consent. When set, Chord will forward this preference to Google as <code>ad_storage</code>, <code>ad_user_data</code>, and <code>ad_personalization</code> signals (Consent Mode v2). Common values: <code>C0004</code> (OneTrust Targeting), <code>targeting</code>. Leave blank to disable Consent Mode v2 for this destination."
      ),
    analyticsConsentCategory: z
      .string()
      .optional()
      .describe(
        "Analytics Consent Category::The consent category key (from your CMP) that controls analytics consent. When set, Chord will forward this preference to Google as the <code>analytics_storage</code> signal (Consent Mode v2). Common values: <code>C0002</code> (OneTrust Performance), <code>analytics</code>. Leave blank to omit the analytics storage signal."
      ),
  }),
  deviceOptions: {
    type: "internal-plugin",
    name: "ga4-tag",
    // type: "analytics-plugin",
    // packageCdn:
    //   "https://cdn.jsdelivr.net/npm/@analytics/google-analytics@1.0.7/dist/@analytics/google-analytics.min.js",
    // moduleVarName: "analyticsGa",
  } as DeviceOptions,
  connectionOptions: DeviceDestinationsConnectionOptions,
};

const googleAdsDestination = {
  id: "google-ads-tag",
  isSynchronous: true,
  icon: googleAdsIcon,
  title: "Google Ads (Gtag)",
  tags: "Device Destinations",
  description:
    "Advertise on Google and put your message in front of potential customers right when they're searching for what you have to offer. <a href='https://ads.google.com' target='_blank' rel='noreferrer noopener'>Google Ads (Gtag) Website.</a>",
  credentials: z.object({
    conversionId: z
      .string()
      .describe(
        "Enter your GOOGLE-CONVERSION-ID. You can get this value from your global site tag snippet. It should look something like AW-901243031"
      ),
    clickConversions: z
      .array(
        z.object({
          eventName: z
            .string()
            .describe(
              "Enter your .track() event name here that you want to explicitly map to a Click Conversion. For example, if you want to map .track('Order Completed'), you can enter Order Completed in this setting."
            ),
          conversionLabel: z
            .string()
            .describe(
              "Enter your conversion label. You can find this value from your event snippet. The provided event snippet should have send_to: 'AW-123456789/AbC-D_efG-h12_34-567'. Enter the part after the '/'"
            ),
          accountId: z
            .string()
            .optional()
            .describe(
              "You can optionally override the top level Google Conversion ID if you need to send this specific conversion to a different Google Ads account. This is useful if you work with multiple third party agencies managing different Google Ads account on your behalf."
            ),
        } as const)
      )
      .optional()
      .describe(
        "You can map your .track() events to specific Google Ads Click Conversions by providing your event name,  Conversion ID and optional Account ID"
      ),
    pageLoadConversions: z
      .array(
        z.object({
          eventName: z
            .string()
            .describe(
              "Enter your .page() name here that you want to explicitly map to a Page Load Conversion. For example, if you want to map .page('Home'), you can enter Home in this setting."
            ),
          conversionLabel: z
            .string()
            .describe(
              "Enter your Conversion Label. You can find this value from your event snippet. The provided event snippet should have send_to: 'AW-123456789/AbC-D_efG-h12_34-567'. Enter the part after the '/'"
            ),
          accountId: z
            .string()
            .optional()
            .describe(
              "You can optionally override the top level Google Conversion ID if you need to send this specific conversion to a different Google Ads account. This is useful if you work with multiple third party agencies managing different Google Ads account on your behalf."
            ),
        } as const)
      )
      .optional()
      .describe(
        "You can map your .track() events to specific Google Ads Click Conversions by providing your event name,  Conversion ID and optional Account ID"
      ),
    disableAdPersonalization: z
      .boolean()
      .default(false)
      .describe(
        "Enable this setting to to disable the collection of remarketing data for users who do not wish to view personalized ads. When enabled, Segment will set the allow_ad_personalization_signals tag parameter to false. For more information about disabling the collection of remarketing data see <a href='https://support.google.com/google-ads/answer/9606827?hl=en' target='_blank' rel='noreferrer noopener'>Google's documentation here.</a>"
      ),
    conversionLinker: z
      .boolean()
      .default(true)
      .describe(
        "If you don't want the global site tag to set first-party cookies on your site's domain, you should disable this setting. Disabling this is NOT recommended by Google as it can lead to less accurate conversion measurements."
      ),
    defaultPageConversion: z
      .string()
      .optional()
      .describe(
        "If you want to map all your default .page() calls that do not explicitly pass in a name to a conversion event, you can enter the Google Ads Conversion ID here."
      ),
    sendPageView: z
      .boolean()
      .default(true)
      .describe(
        "If you want to prevent the global site tag from automatically sending a remarketing hit to your Google Ads accounts when the page is viewed, you can disable this setting. Otherwise, by default all properties sent with the conversions will be sent as remarketing hits which will allow you to create audiences based on those properties."
      ),
    floodlightAccountId: z
      .string()
      .optional()
      .describe(
        'Enter your DoubleClick Floodlight Advertiser ID to have it passed to Gtag\'s <code class="language-plaintext highlighter-rouge">config</code> parameter. This ensures tags are loaded by first-party cookies. This value should look like DC-1234567. For more information, see <a href="https://support.google.com/campaignmanager/answer/7554821?hl=en" target="_blank" rel="noreferrer noopener">Google\'s documentation</a>.'
      ),
    adConsentCategory: z
      .string()
      .optional()
      .describe(
        "Ad Consent Category::The consent category key (from your CMP) that controls advertising consent. When set, Chord will forward this preference to Google as <code>ad_storage</code>, <code>ad_user_data</code>, and <code>ad_personalization</code> signals (Consent Mode v2). Common values: <code>C0004</code> (OneTrust Targeting), <code>targeting</code>. Leave blank to disable Consent Mode v2 for this destination."
      ),
    analyticsConsentCategory: z
      .string()
      .optional()
      .describe(
        "Analytics Consent Category::The consent category key (from your CMP) that controls analytics consent. When set, Chord will forward this preference to Google as the <code>analytics_storage</code> signal (Consent Mode v2). Common values: <code>C0002</code> (OneTrust Performance), <code>analytics</code>. Leave blank to omit the analytics storage signal."
      ),
    sendRemarketingEvents: z
      .boolean()
      .default(false)
      .describe(
        "Send Dynamic Remarketing Events::Send Product List Viewed, Products Searched, Product Viewed, Product Added and Order Completed to your Google Ads account as dynamic remarketing events, so Google Ads can show shoppers the products they interacted with. Configured conversions are unaffected."
      ),
    // Since this enum is not exported/inferred in the destination code, any changes to this enum must also be made
    // to the type in the destination implementation.
    remarketingProductIdFormat: z
      .enum(["product_id", "sku", "variant_id", "shopify"])
      .default("product_id")
      .describe(
        "Remarketing Product ID Format::The product ID format in your Merchant Center feed. Item IDs must match the feed exactly. Choose <code>shopify</code> for feeds created by Shopify's Google channel (<code>shopify_us_&lt;product_id&gt;_&lt;variant_id&gt;</code>)."
      ),
    remarketingFeedCountry: z
      .string()
      .default("US")
      .describe(
        "Remarketing Feed Country::Two-letter country code in your Shopify feed IDs. Used when the product ID format is <code>shopify</code>. Defaults to <code>US</code>."
      ),
  }),
  deviceOptions: {
    type: "internal-plugin",
    name: "google-ads-tag",
  } as DeviceOptions,
  connectionOptions: DeviceDestinationsConnectionOptions,
};

const gtmDeviceDestination = {
  id: "gtm",
  isSynchronous: true,
  icon: gtmIcon,
  title: "Google Tag Manager",
  tags: "Device Destinations",
  description: "Installs Google Tag Manager client code and sends events to Google Tag Manager.",
  credentials: z.object({
    containerId: z.string().describe("The Container ID uniquely identifies the GTM Container."),
    dataLayerName: z.string().default("dataLayer").describe("The name of the data layer variable."),
    resetDataLayer: z
      .boolean()
      .default(true)
      .describe(
        "Reset Data Layer::Clear the GTM Data Layer model after each event. Leave enabled to keep events isolated. Disable so pushed values persist to later events, which is what most containers (and Segment) expect."
      ),
  }),
  deviceOptions: {
    type: "internal-plugin",
    name: "gtm",
  } as DeviceOptions,
  connectionOptions: DeviceDestinationsConnectionOptions,
};

const facebookPixelDeviceDestination = {
  id: "facebook-pixel",
  isSynchronous: true,
  icon: facebookIcon,
  title: "Facebook Pixel",
  tags: "Device Destinations",
  description: "Tracks users in Facebook with client side code snippet.",
  credentials: z.object({
    pixelId: z
      .string()
      .optional()
      .describe("Pixel ID (legacy)::Single Pixel ID. Use pixelIds for multi-pixel support."),
    pixelIds: z
      .array(z.string())
      .optional()
      .describe(
        "Pixel IDs::One or more Pixel IDs from the <a href='https://www.facebook.com/ads/manager/pixel/facebook_pixel/' target='_blank' rel='noreferrer noopener'>Facebook Pixel creation page</a>. Supports multiple pixels."
      ),
    // Since this enum is not exported/inferred in the destination code, any changes to this enum must also be made
    // to the type in the destination implementation.
    productContentIdField: z
      .enum(["sku", "product_id", "variant_id"])
      .default("sku")
      .describe("Specify which field to use as content ID for product-related events."),
  }),
  deviceOptions: {
    type: "internal-plugin",
    name: "facebook-pixel",
  } as DeviceOptions,
  connectionOptions: DeviceDestinationsConnectionOptions,
};

const chordRevealDeviceDestination = {
  id: "customersai-pixel",
  isSynchronous: true,
  icon: ChordIconBlack,
  title: "Chord Reveal",
  tags: "Device Destinations",
  description: "Support identification of anonymous visitors to your site.",
  credentials: z.object({
    scriptUrl: z
      .string()
      .describe(
        "Script URL::The brand's Customers.ai pixel script URL, printed by the Identifier service's register_customersai_subaccount script."
      ),
  }),
  deviceOptions: {
    type: "internal-plugin",
    name: "customersai-pixel",
  } as DeviceOptions,
  connectionOptions: DeviceDestinationsConnectionOptions,
};

const awinDeviceDestination = {
  id: "share-a-sale-pixel",
  isSynchronous: true,
  icon: awinPixelIcon,
  title: "AWIN Pixel",
  tags: "Device Destinations",
  description: "Tracks users in AWIN (formerly ShareASale) with client side code snippet.",
  credentials: z.object({
    merchantId: z
      .string()
      .describe(
        "your AWIN Merchant ID. You can find this at the top-left of your AWIN account when you are logged in."
      ),
  }),
  deviceOptions: {
    type: "internal-plugin",
    name: "share-a-sale-pixel",
  } as DeviceOptions,
  connectionOptions: DeviceDestinationsConnectionOptions,
};

const impactDeviceDestination = {
  id: "impact",
  isSynchronous: true,
  icon: impactIcon,
  title: "Impact (Device Mode)",
  tags: "Device Destinations",
  description: "Tracks conversions and identifies visitors via the Impact Universal Tracking Tag (UTT).",
  credentials: z.object({
    accountId: z
      .string()
      .describe(
        "Account ID::Your Impact account ID, used to load the UTT script. Found in your Impact account settings."
      ),
    eventTypeId: z
      .string()
      .optional()
      .describe("Event Type ID::The numeric event type ID passed to trackConversion calls."),
    actionEventNames: z
      .array(z.string())
      .optional()
      .describe(
        "Conversion Event Names::Track event names that trigger a trackConversion call. Defaults to 'Order Completed' when empty."
      ),
  }),
  deviceOptions: {
    type: "internal-plugin",
    name: "impact",
  } as DeviceOptions,
  connectionOptions: DeviceDestinationsConnectionOptions,
};

const twitterAdsDestination = {
  id: "twitter-ads",
  isSynchronous: true,
  icon: twitterIcon,
  title: "Twitter Ads",
  tags: "Device Destinations",
  description:
    "Track conversions and website activity for Twitter Ads campaigns with both Universal Tag and legacy pixel support.",
  credentials: meta.TwitterAdsCredentials,
  deviceOptions: {
    type: "internal-plugin",
    name: "twitter-ads",
  } as DeviceOptions,
  connectionOptions: DeviceDestinationsConnectionOptions,
};

const heapDeviceDestination = {
  id: "heap-pixel",
  isSynchronous: true,
  icon: heapIcon,
  title: "Heap (Device Mode)",
  tags: "Device Destinations",
  description: "Sends events directly to Heap via the Heap browser SDK.",
  credentials: meta.HeapDeviceCredentials,
  deviceOptions: { type: "internal-plugin", name: "heap-pixel" } as DeviceOptions,
  connectionOptions: DeviceDestinationsConnectionOptions,
};

const brazeDeviceDestination = {
  id: "braze-pixel",
  isSynchronous: true,
  icon: brazeIcon,
  title: "Braze (Device Mode)",
  tags: "Device Destinations",
  description: "Sends data directly to Braze's API with Braze's client side SDK.",
  credentials: meta.BrazeDeviceCredentials,
  deviceOptions: { type: "internal-plugin", name: "braze-pixel" } as DeviceOptions,
  connectionOptions: DeviceDestinationsConnectionOptions,
};

const tiktokPixelDestination = {
  id: "tiktok-pixel",
  isSynchronous: true,
  icon: tikTokIcon,
  title: "TikTok Pixel",
  tags: "Device Destinations",
  description:
    "The TikTok Pixel allows you to track actions people take on your website after viewing your TikTok ads.",
  credentials: meta.TikTokPixelCredentials,
  deviceOptions: { type: "internal-plugin", name: "tiktok-pixel" } as DeviceOptions,
  connectionOptions: DeviceDestinationsConnectionOptions,
};

const bingAdsDeviceDestination = {
  id: "bing-ads",
  isSynchronous: true,
  icon: bingIcon,
  title: "Bing Ads (UET Tag)",
  tags: "Device Destinations",
  description:
    "Tracks website activity for Microsoft Advertising (Bing Ads) using the Universal Event Tracking (UET) tag.",
  credentials: meta.BingAdsCredentials,
  deviceOptions: {
    type: "internal-plugin",
    name: "bing-ads",
  } as DeviceOptions,
  connectionOptions: DeviceDestinationsConnectionOptions,
};

const contentsquarePixelDestination = {
  id: "contentsquare-pixel",
  isSynchronous: true,
  icon: contentsquareIcon,
  title: "Contentsquare (Hotjar)",
  tags: "Device Destinations",
  description:
    "Contentsquare (formerly Hotjar) provides experience analytics and behavior insights to understand how users interact with your website.",
  credentials: meta.ContentsquarePixelCredentials,
  deviceOptions: {
    type: "internal-plugin",
    name: "contentsquare-pixel",
  } as DeviceOptions,
  connectionOptions: DeviceDestinationsConnectionOptions,
};

const ninetailedDestination = {
  id: "ninetailed",
  isSynchronous: true,
  icon: ninetailedIcon,
  title: "Ninetailed",
  tags: "Device Destinations",
  description: "Ninetailed is a headless personalization & A/B testing solution for Jamstack websites.",
  credentials: meta.NinetailedCredentials,
  deviceOptions: {
    type: "internal-plugin",
    name: "ninetailed",
  } as DeviceOptions,
  connectionOptions: DeviceDestinationsConnectionOptions,
};

const tripleWhaleDestination = {
  id: "triple-whale",
  isSynchronous: true,
  icon: tripleWhaleIcon,
  title: "Triple Whale",
  tags: "Device Destinations",
  description: "Triple Whale is a comprehensive analytics and attribution platform for e-commerce businesses.",
  credentials: z.object({
    storeIdentifier: z.string().describe("Store Identifier::Your unique store identifier provided by Triple Whale"),
    platform: z
      .enum(["SHOPIFY", "CUSTOM"])
      .default("CUSTOM")
      .describe(
        "Platform Type::Select 'SHOPIFY' if you are using a Shopify Headless store. Select 'CUSTOM' for all other store frameworks (e.g., custom builds, Magento, WooCommerce, etc.)."
      ),
    debug: z.boolean().default(false).describe("Enable debug logging for Triple Whale events"),
  }),
  deviceOptions: {
    type: "internal-plugin",
    name: "triple-whale",
  } as DeviceOptions,
  connectionOptions: DeviceDestinationsConnectionOptions,
};

const amplitudePixelDestination = {
  id: "amplitude",
  isSynchronous: true,
  icon: amplitudeIcon,
  title: "Amplitude (Device Mode)",
  tags: "Device Destinations",
  description:
    "Loads Amplitude SDK client-side for session tracking and real-time event capture. Session IDs are automatically shared with cloud-mode Amplitude destination.",
  credentials: meta.AmplitudePixelCredentials,
  credentialsUi: meta.AmplitudePixelCredentialsUi,
  deviceOptions: {
    type: "internal-plugin",
    name: "amplitude",
  } as DeviceOptions,
  connectionOptions: DeviceDestinationsConnectionOptions,
};

const axonDestination = {
  id: "axon",
  isSynchronous: true,
  icon: axonIcon,
  title: "Axon Analytics",
  tags: "Device Destinations",
  description: "Axon Analytics is a marketing attribution and analytics platform for e-commerce businesses.",
  credentials: z.object({
    eventKey: z.string().describe("Event Key::Your Axon event key for tracking"),
  }),
  deviceOptions: {
    type: "internal-plugin",
    name: "axon",
  } as DeviceOptions,
  connectionOptions: DeviceDestinationsConnectionOptions,
};

const northbeamPixelDestination = {
  id: "northbeam-pixel",
  isSynchronous: true,
  icon: northbeamPixelIcon,
  title: "Northbeam Pixel",
  tags: "Device Destinations",
  description:
    "Loads the Northbeam JavaScript pixel in the browser for page view tracking, identity resolution, and ecommerce event attribution.",
  credentials: z.object({
    dataClientId: z
      .string()
      .describe(
        "Data Client ID::A UUID used to identify your account. Can be found in the Northbeam dashboard under Settings > API Keys."
      ),
    productViewedGoalId: z
      .string()
      .optional()
      .default("product_viewed")
      .describe(
        "Product Viewed Goal ID::Custom goal ID registered with Northbeam for Product Viewed events. Must match what is registered in your Northbeam account."
      ),
    addToCartGoalId: z
      .string()
      .optional()
      .default("add_to_cart")
      .describe(
        "Add to Cart Goal ID::Custom goal ID registered with Northbeam for Product Added events. Must match what is registered in your Northbeam account."
      ),
    checkoutStartedGoalId: z
      .string()
      .optional()
      .default("checkout_initiated")
      .describe(
        "Checkout Started Goal ID::Custom goal ID registered with Northbeam for Checkout Started events. Must match what is registered in your Northbeam account."
      ),
  }),
  deviceOptions: {
    type: "internal-plugin",
    name: "northbeam-pixel",
  } as DeviceOptions,
  connectionOptions: DeviceDestinationsConnectionOptions,
};

const openAiAdsPixelDestination = {
  id: "openai-ads-pixel",
  isSynchronous: true,
  icon: openAiIcon,
  title: "OpenAI Ads Pixel",
  tags: "Device Destinations",
  description:
    "Loads the OpenAI Ads (ChatGPT Ads) measurement pixel in the browser, capturing the oppref click identifier and forwarding page views, product, and purchase events to OpenAI.",
  credentials: meta.OpenAiAdsPixelCredentials,
  deviceOptions: {
    type: "internal-plugin",
    name: "openai-ads-pixel",
  } as DeviceOptions,
  connectionOptions: DeviceDestinationsConnectionOptions,
};

const nostoTagDestination = {
  id: "nosto-tag",
  isSynchronous: true,
  icon: nostoIcon,
  title: "Nosto Tag",
  tags: "Device Destinations",
  description:
    "Loads Nosto's onsite personalization library in the browser, keeping the visitor's Nosto session up to date with page views, cart contents and identity, and rendering recommendations into the placements already present on the page. Replaces a manually installed Nosto script — do not run both.",
  credentials: meta.NostoTagCredentials,
  deviceOptions: {
    type: "internal-plugin",
    name: "nosto-tag",
  } as DeviceOptions,
  connectionOptions: DeviceDestinationsConnectionOptions,
};

const roktPixelDestination = {
  id: "rokt-pixel",
  isSynchronous: true,
  icon: roktIcon,
  title: "Rokt Pixel",
  tags: "Device Destinations",
  description:
    "Loads Rokt's Web SDK in the browser and records conversions and page views for attribution and retargeting audiences. Safe to run alongside the Rokt Events API destination — Rokt deduplicates on the confirmation reference both send.",
  credentials: meta.RoktPixelCredentials,
  credentialsUi: meta.RoktPixelCredentialsUi,
  deviceOptions: {
    type: "internal-plugin",
    name: "rokt-pixel",
  } as DeviceOptions,
  connectionOptions: DeviceDestinationsConnectionOptions,
};

const redditPixelDestination = {
  id: "reddit-pixel",
  isSynchronous: true,
  icon: redditIcon,
  title: "Reddit Pixel",
  tags: "Device Destinations",
  description:
    "Loads the Reddit conversion pixel in the browser and forwards page views, product, and purchase events to Reddit Ads for attribution and campaign optimization.",
  credentials: meta.RedditPixelCredentials,
  deviceOptions: {
    type: "internal-plugin",
    name: "reddit-pixel",
  } as DeviceOptions,
  connectionOptions: DeviceDestinationsConnectionOptions,
};

const dotdigitalTagDestination = {
  id: "dotdigital-tag",
  isSynchronous: true,
  icon: dotdigitalIcon,
  title: "Dotdigital Tag",
  tags: "Device Destinations",
  description:
    "Loads the Dotdigital Tag in the browser for web behaviour tracking, browse abandonment and cart abandonment, and reports product views, cart updates, checkouts and orders to Dotdigital. Replaces a manually installed Dotdigital Tag — do not run both.",
  credentials: meta.DotdigitalTagCredentials,
  deviceOptions: {
    type: "internal-plugin",
    name: "dotdigital-tag",
  } as DeviceOptions,
  connectionOptions: DeviceDestinationsConnectionOptions,
};

const snapPixelDestination = {
  id: "snap-pixel",
  isSynchronous: true,
  icon: snapchatIcon,
  title: "Snap Pixel",
  tags: "Device Destinations",
  description:
    "The Snap Pixel allows you to track actions people take on your website after viewing your Snapchat ads.",
  credentials: meta.SnapPixelCredentials,
  deviceOptions: {
    type: "internal-plugin",
    name: "snap-pixel",
  } as DeviceOptions,
  connectionOptions: DeviceDestinationsConnectionOptions,
};

const stackAdaptPixelDestination = {
  id: "stackadapt-pixel",
  isSynchronous: true,
  icon: stackAdaptIcon,
  title: "StackAdapt Pixel",
  tags: "Device Destinations",
  description:
    "Tracks users with StackAdapt Universal Pixel for retargeting, conversion tracking, and lookalike audiences.",
  credentials: z.object({
    pixelId: z
      .string()
      .describe("Universal Pixel ID::Your StackAdapt Universal Pixel ID from the Pixels page in StackAdapt."),
  }),
  deviceOptions: {
    type: "internal-plugin",
    name: "stackadapt-pixel",
  } as DeviceOptions,
  connectionOptions: DeviceDestinationsConnectionOptions,
};

const spotifyAdAnalyticsPixelDestination = {
  id: "spotify-ad-analytics-pixel",
  isSynchronous: true,
  icon: spotifyIcon,
  title: "Spotify Ad Analytics Pixel",
  tags: "Device Destinations",
  description:
    "Spotify Ad Analytics (formerly known as Podsights) is Spotify's audio ad attribution platform. This pixel tracks page views, product views, add-to-cart, lead, and purchase events to attribute conversions back to podcast and audio campaigns served on Spotify.",
  credentials: meta.SpotifyAdAnalyticsPixelCredentials,
  deviceOptions: {
    type: "internal-plugin",
    name: "spotify-ad-analytics-pixel",
  } as DeviceOptions,
  connectionOptions: DeviceDestinationsConnectionOptions,
};

const shopMyPixelDestination = {
  id: "shopmy-pixel",
  isSynchronous: true,
  icon: shopMyIcon,
  title: "ShopMy",
  tags: "Device Destinations",
  description: "Track affiliate conversions with ShopMy creator commerce platform.",
  credentials: z.object({}),
  deviceOptions: {
    type: "internal-plugin",
    name: "shopmy-pixel",
  } as DeviceOptions,
  connectionOptions: DeviceDestinationsConnectionOptions,
};

const postieDestination = {
  id: "postie",
  isSynchronous: true,
  icon: postieIcon,
  title: "Postie",
  tags: "Device Destinations",
  description: "Postie is a direct mail marketing platform that tracks conversions and email captures.",
  credentials: z.object({
    accountId: z
      .string()
      .describe(
        "Account ID::Your Postie account ID. This can be found in the script URL from your Postie dashboard (e.g., if script URL is //scripts.postie.com/ylmafkcp/lp.1.js, the account ID is ylmafkcp)."
      ),
  }),
  deviceOptions: {
    type: "internal-plugin",
    name: "postie",
  } as DeviceOptions,
  connectionOptions: DeviceDestinationsConnectionOptions,
};

const retentionDestination = {
  id: "retention",
  isSynchronous: true,
  icon: retentionIcon,
  title: "Retention.com",
  tags: "Device Destinations",
  description:
    "Retention.com helps identify anonymous website visitors and recover abandoned carts through email marketing.",
  credentials: z.object({
    siteId: z
      .string()
      .describe(
        "Site ID::Your Retention.com Site ID. This can be found in the script URL from your Retention.com dashboard (e.g., if script URL is https://s.retention.com/pixel/abc123.js, the Site ID is abc123)."
      ),
  }),
  deviceOptions: {
    type: "internal-plugin",
    name: "retention",
  } as DeviceOptions,
  connectionOptions: DeviceDestinationsConnectionOptions,
};

const theTradeDeskPixelDestination = {
  id: "thetradedesk-pixel",
  isSynchronous: true,
  icon: theTradeDeskIcon,
  title: "The Trade Desk Universal Pixel",
  tags: "Device Destinations",
  description:
    "Track conversions and website activity for The Trade Desk programmatic advertising platform. Fires on page views and Order Completed events.",
  credentials: meta.TheTradeDeskPixelCredentials,
  deviceOptions: {
    type: "internal-plugin",
    name: "thetradedesk-pixel",
  } as DeviceOptions,
  connectionOptions: DeviceDestinationsConnectionOptions,
};

const yahooPixelDestination = {
  id: "yahoo-pixel",
  isSynchronous: true,
  icon: yahooIcon,
  title: "Yahoo DPA Pixel",
  tags: "Device Destinations",
  description: "Track website activity and conversions using the Yahoo Dynamic Product Ads (DPA) pixel.",
  credentials: z.object({
    pixelId: z.string().describe("Pixel ID::Your Yahoo Pixel ID from your Dot page in the Yahoo DSP dashboard."),
    projectId: z.string().describe("Project ID::Your Yahoo Project ID from your Dot page in the Yahoo DSP dashboard."),
  }),
  deviceOptions: {
    type: "internal-plugin",
    name: "yahoo-pixel",
  } as DeviceOptions,
  connectionOptions: DeviceDestinationsConnectionOptions,
};

const mntnTrackingPixelDestination = {
  id: "mntn-tracking-pixel",
  isSynchronous: true,
  icon: mntnIcon,
  title: "MNTN Tracking Pixel",
  tags: "Device Destinations",
  description: "Fires the MNTN tracking pixel site-wide on every page for audience building and attribution.",
  credentials: z.object({
    advertiserId: z
      .string()
      .describe(
        "Advertiser ID::Your MNTN Advertiser ID (the value of the 'o' variable in your tracking pixel snippet)."
      ),
  }),
  deviceOptions: {
    type: "internal-plugin",
    name: "mntn-tracking-pixel",
  } as DeviceOptions,
  connectionOptions: DeviceDestinationsConnectionOptions,
};

const mntnConversionPixelDestination = {
  id: "mntn-conversion-pixel",
  isSynchronous: true,
  icon: mntnIcon,
  title: "MNTN Conversion Pixel",
  tags: "Device Destinations",
  description:
    "Fires the MNTN conversion pixel on purchase, sign-up, and lead events for accurate CTV attribution and ROAS reporting.",
  credentials: z.object({
    advertiserId: z
      .string()
      .describe(
        "Advertiser ID::Your MNTN Advertiser ID (the value of the 'o' variable in your conversion pixel snippet)."
      ),
    conversionType: z
      .string()
      .optional()
      .describe(
        "Conversion Type Override::Optional override for the conversion_type parameter. If not set, it is auto-mapped from the event name (Order Completed → Purchase, Signed Up → SignUp, Lead Generated → Lead)."
      ),
    enableOrderCompletedCalls: z
      .boolean()
      .default(true)
      .describe("Enable Order Completed::Fire the conversion pixel on Order Completed events."),
    enableTrackCalls: z
      .boolean()
      .default(false)
      .describe("Enable Other Track Events::Fire the conversion pixel on other track events (e.g. Sign Up, Lead)."),
  }),
  deviceOptions: {
    type: "internal-plugin",
    name: "mntn-conversion-pixel",
  } as DeviceOptions,
  connectionOptions: DeviceDestinationsConnectionOptions,
};

const sfmcCollectDestination = {
  id: "sfmc-collect",
  isSynchronous: true,
  icon: salesforceIcon,
  title: "Salesforce Marketing Cloud Collect.js",
  tags: "Device Destinations",
  description:
    "Loads SFMC Collect.js (Web Collect) in the browser and maps Chord events to Collect.js tracking calls for use in Einstein Recommendations, Journey Builder, and attribution.",
  credentials: z.object({
    mid: z
      .string()
      .describe(
        "Marketing Cloud Member ID (MID)::Your Salesforce Marketing Cloud Member ID. Found in Setup > Account Settings. Used to load the Collect.js script and set the org ID."
      ),
  }),
  deviceOptions: {
    type: "internal-plugin",
    name: "sfmc-collect",
  } as DeviceOptions,
  connectionOptions: DeviceDestinationsConnectionOptions,
};

export const coreDestinations: DestinationType<any>[] = [
  tagDestination,
  pinterestTagDestination,
  facebookPixelDeviceDestination,
  gaDeviceDestination,
  gtmDeviceDestination,
  googleAdsDestination,
  logRocketDestination,
  heapDeviceDestination,
  brazeDeviceDestination,
  tiktokPixelDestination,
  twitterAdsDestination,
  impactDeviceDestination,
  awinDeviceDestination,
  bingAdsDeviceDestination,
  contentsquarePixelDestination,
  ninetailedDestination,
  tripleWhaleDestination,
  amplitudePixelDestination,
  axonDestination,
  snapPixelDestination,
  openAiAdsPixelDestination,
  nostoTagDestination,
  northbeamPixelDestination,
  redditPixelDestination,
  roktPixelDestination,
  dotdigitalTagDestination,
  stackAdaptPixelDestination,
  spotifyAdAnalyticsPixelDestination,
  shopMyPixelDestination,
  postieDestination,
  retentionDestination,
  theTradeDeskPixelDestination,
  yahooPixelDestination,
  mntnTrackingPixelDestination,
  mntnConversionPixelDestination,
  chordRevealDeviceDestination,
  sfmcCollectDestination,
  {
    id: "clickhouse",
    usesBulker: true,
    icon: <ClickhouseIcon />,
    connectionOptions: BaseBulkerConnectionOptions.merge(
      z.object({
        primaryKey: z.string().default("timestamp,message_id"),
        clickhouseSettings: z.string().default(""),
      })
    ).describe(
      JSON.stringify({
        limitations: {
          streamModeLocked:
            'Stream mode in ClickHouse requires async inserts enabled both on client and server level. <a href="https://clickhouse.com/docs/en/optimize/asynchronous-inserts" target="_blank" rel="noopener noreferrer">Read more about async inserts here</a>.<br/>Also, make sure to set async inserts parameters in the advanced section',
        },
      })
    ),
    title: "Clickhouse",
    tags: "Data Warehouse",
    credentials: ClickhouseCredentials,
    description:
      "ClickHouse is an open-source column-oriented database management system specialized for online analytical processing of queries (OLAP).",
    credentialsUi: {
      hosts: {
        editor: "StringArrayEditor",
      },
      loadAsJson: {
        hidden: true,
      },
      password: {
        password: true,
      },
    },
  },
  {
    id: "postgres",
    usesBulker: true,
    icon: postgresIcon,
    title: "Postgres",
    tags: "Data Warehouse",
    connectionOptions: BaseBulkerConnectionOptions,
    credentials: z.object({
      authenticationMethod: z
        .enum(["password", "google-psc"])
        .optional()
        .default("password")
        .describe(
          "Authentication Method::Standard username/password or <a target='_blank' rel='noreferrer noopener' href='https://docs.jitsu.com/destinations/warehouse/postgres#advanced-private-service-connect-for-google-cloud-sql'>Private Service Connect</a> for Google-managed postgres instances only."
        ),
      instanceConnectionName: z
        .string()
        .optional()
        .describe(
          "Instance Connection Name::Google SQL instance Connection Name in the format <code>project-name:region:instance-name</code>. <a target='_blank' rel='noreferrer noopener' href='https://docs.jitsu.com/destinations/warehouse/postgres#provide-jitsu-with-cloud-sql-instance-details'>How to obtain</a>."
        ),
      host: z.string().optional().describe("Postgres host"),
      port: z.number().optional().default(5432).describe("Postgres port"),
      sslMode: z
        .enum(["disable", "require", "verify-ca", "verify-full"])
        .optional()
        .default("require")
        .describe(
          "SSL Mode::SSL mode for Postgres connection: <code>disable</code>,<code>require</code>,<code>verify-ca</code>,<code>verify-full</code>"
        ),
      sslServerCA: z.string().optional().describe("SSL Server CA::"),
      sslClientCert: z.string().optional().describe("SSL Client Cert::"),
      sslClientKey: z.string().optional().describe("SSL Client Key::"),
      database: z.string().describe("Postgres database name"),
      username: z.string().optional().describe("Postgres username"),
      password: z.string().optional().describe("Postgres password"),
      defaultSchema: z.string().default("public").describe("Schema::Postgres schema"),
    }),
    credentialsUi: {
      authenticationMethod: {
        correction: obj => obj.authenticationMethod || "password",
      },
      instanceConnectionName: {
        hidden: obj => obj.authenticationMethod !== "google-psc",
      },
      username: {
        hidden: obj => obj.authenticationMethod === "google-psc",
      },
      password: {
        password: true,
        hidden: obj => obj.authenticationMethod === "google-psc",
      },
      host: {
        hidden: obj => obj.authenticationMethod === "google-psc",
      },
      port: {
        hidden: obj => obj.authenticationMethod === "google-psc",
      },
      sslMode: {
        hidden: obj => obj.authenticationMethod === "google-psc",
      },
      sslServerCA: {
        textarea: true,
        hidden: obj => obj.sslMode === "disable" || obj.sslMode === "require",
      },
      sslClientCert: {
        textarea: true,
        hidden: obj => obj.sslMode === "disable" || obj.sslMode === "require",
      },
      sslClientKey: {
        textarea: true,
        hidden: obj => obj.sslMode === "disable" || obj.sslMode === "require",
      },
    },
    description: "Postgres is a powerful, open source object-relational database system.",
  },
  {
    id: "bigquery",
    usesBulker: true,
    icon: bigqueryIcon,
    connectionOptions: BaseBulkerConnectionOptions.describe(
      JSON.stringify({
        limitations: {
          streamModeDisabled:
            "It's possible to implement stream mode for BigQuery, but data Deduplication cannot be supported in this mode. So it is currently disabled in Jitsu.",
        },
      })
    ),
    title: "BigQuery",
    tags: "Data Warehouse",
    description: "BigQuery is a cloud-based SQL data warehouse service developed by Google.",
    credentials: z.object({
      project: z
        .string()
        .describe(
          "Project ID::Google Cloud Project ID. <a target='_blank' rel='noreferrer noopener' href='https://support.google.com/googleapi/answer/7014113?hl=en'>Locate Project ID</a>"
        ),
      bqDataset: z
        .string()
        .describe(
          "Dataset::BigQuery <a target='_blank' rel='noreferrer noopener' href='https://cloud.google.com/bigquery/docs/datasets-intro'>Dataset</a>"
        ),
      keyFile: z
        .string()
        .describe(
          "Access Key::Google Service Account JSON for BigQuery. <a target='_blank' rel='noreferrer noopener' href='https://jitsu.com/docs/configuration/google-authorization#service-account-configuration'>Read more about Google Authorization</a>"
        ),
    }),
    credentialsUi: {
      keyFile: {
        editor: "CodeEditor",
        editorProps: {
          language: "json",
          height: "250px",
          monacoOptions: { lineNumbers: "off" },
        },
      },
    },
  },
  {
    id: "snowflake",
    usesBulker: true,
    title: "Snowflake",
    tags: "Data Warehouse",
    credentials: z.object({
      authenticationMethod: z
        .enum(["key-pair", "password"])
        .optional()
        .default("key-pair")
        .describe(
          "Authentication Method::Snowflake authentication method: <a target='_blank' rel='noopener noreferrer' href='https://docs.snowflake.com/en/user-guide/key-pair-auth'>Key-pair</a> or username/password"
        ),
      username: z.string().optional().describe("Snowflake username"),
      password: z.string().optional().describe("Snowflake password"),
      privateKey: z
        .string()
        .optional()
        .describe(
          "Private Key::Snowflake private key. <a target='_blank' rel='noopener noreferrer' href='https://docs.snowflake.com/en/user-guide/key-pair-auth'>Generate the private key</a>"
        ),
      privateKeyPassphrase: z.string().optional(),
      account: z.string().describe("Snowflake account name"),
      warehouse: z.string().describe("Snowflake warehouse name"),
      database: z.string().describe("Snowflake database name"),
      defaultSchema: z.string().default("PUBLIC").describe("Schema::Snowflake schema"),
      parameters: z
        .object({})
        .catchall(z.string().default(""))
        .optional()
        .describe("Additional Snowflake connection parameters"),
    }),
    credentialsUi: {
      authenticationMethod: {
        correction: (obj, isNew) => (isNew ? "key-pair" : obj.authenticationMethod || "password"),
      },
      password: {
        password: true,
        hidden: obj => obj.authenticationMethod === "key-pair",
      },
      privateKey: {
        hidden: obj => obj.authenticationMethod !== "key-pair",
        textarea: true,
        password: true,
      },
      privateKeyPassphrase: {
        hidden: obj => obj.authenticationMethod !== "key-pair",
        password: true,
      },
    },
    connectionOptions: BaseBulkerConnectionOptions,
    icon: snowflakeIcon,
    description: "Snowflake is an independent a cloud data warehouse with compute-based pricing.",
  },
  {
    id: "redshift",
    usesBulker: true,
    icon: redshiftIcon,
    title: "Redshift",
    connectionOptions: BaseBulkerConnectionOptions.describe(
      JSON.stringify({
        limitations: {
          streamModeLocked:
            "Supported as plain insert statements.<br/>Don't use at production scale (more than 10 records per minute)",
        },
      })
    ),
    tags: "Data Warehouse",
    credentials: z.object({
      authenticationMethod: z
        .enum(["iam", "password"])
        .optional()
        .default("password")
        .describe(
          "Authentication Method::Redshift authentication method: <a target='_blank' rel='noopener noreferrer' href='https://docs.jitsu.com/destinations/warehouse/redshift#advanced-iam-role-for-jitsu'>IAM Role based</a> or database username/password"
        ),
      // default for the form only: zod 4 would apply .default(false).optional() to a missing value, zod 3 never did
      serverless: z.boolean().optional().meta({ default: false }).describe("Redshift Serverless::"),
      region: z.enum(s3Regions).describe("Region::Aws Region"),
      clusterIdentifier: z.string().optional().describe("Cluster Identifier::Redshift cluster identifier"),
      workgroupName: z.string().optional().describe("Workgroup name::Redshift Serverless workgroup name"),
      roleARN: z
        .string()
        .optional()
        .describe(
          "Role ARN::IAM role ARN. <a target='_blank' rel='noopener noreferrer' href='https://docs.jitsu.com/destinations/warehouse/redshift#advanced-iam-role-for-jitsu'>How to create</a>"
        ),
      externalID: z.string().optional().describe("External ID::IAM external ID"),
      host: z.string().optional().describe("Redshift host"),
      database: z.string().describe("Redshift database name"),
      defaultSchema: z.string().default("PUBLIC").describe("Schema::Redshift schema"),
      username: z.string().optional().describe("Redshift username"),
      password: z.string().optional().describe("Redshift password"),
      bucket: z.string().describe("S3 Bucket Name::S3 Bucket Name"),
      accessKeyId: z
        .string()
        .optional()
        .describe(
          "S3 Access Key Id::S3 Access Key Id. <a target='_blank' rel='noreferrer noopener' href='https://docs.aws.amazon.com/IAM/latest/UserGuide/security-creds.html#access-keys-and-secret-access-keys'>Create access key</a>"
        ),
      secretAccessKey: z.string().optional().describe("S3 Secret Access Key::S3 Secret Access Key"),
    }),
    credentialsUi: {
      authenticationMethod: {
        correction: obj => obj.authenticationMethod || "password",
      },
      username: {
        hidden: obj => obj.authenticationMethod === "iam" && obj.serverless === true,
      },
      password: {
        password: true,
        hidden: obj => obj.authenticationMethod === "iam",
      },
      secretAccessKey: {
        hidden: obj => obj.authenticationMethod === "iam",
        password: true,
      },
      accessKeyId: {
        hidden: obj => obj.authenticationMethod === "iam",
      },
      host: {
        hidden: obj => obj.authenticationMethod === "iam",
      },
      clusterIdentifier: {
        hidden: obj => obj.authenticationMethod !== "iam" || obj.serverless === true,
      },
      workgroupName: {
        hidden: obj => obj.authenticationMethod !== "iam" || obj.serverless === false,
      },
      roleARN: {
        hidden: obj => obj.authenticationMethod !== "iam",
      },
      externalID: {
        // constants are not yet refreshed on form state change. so it is unconditional here
        constant: obj => obj.workspaceId,
      },
    },
    description:
      "Amazon Redshift is a cloud data warehouse that is optimized for the analytical workloads of business intelligence (BI) and data warehousing (DWH). Jitsu supports both Serverless and Classic Redshift",
  },
  {
    id: "duckdb",
    usesBulker: true,
    icon: motherduckIcon,
    title: "MotherDuck (DuckDB)",
    tags: "Data Warehouse",
    connectionOptions: BaseBulkerConnectionOptions,
    credentials: z.object({
      database: z.string().describe("MotherDuck database name"),
      motherduckToken: z
        .string()
        .describe(
          "MotherDuck token::MotherDuck token can be obtained in the MotherDuck's <a target='_blank' rel='noopener noreferrer' href='https://app.motherduck.com/settings/tokens'>Settings -> Access Tokens</a> section"
        ),
      defaultSchema: z.string().default("main").describe("Schema::Database schema"),
    }),
    credentialsUi: {
      motherduckToken: {
        password: true,
      },
    },
    description: "DuckDB-powered cloud data warehouse scaling to terabytes with ease.",
  },
  {
    id: "mysql",
    usesBulker: true,
    icon: mysqlIcon,
    title: "Mysql",
    tags: "Data Warehouse",
    connectionOptions: BaseBulkerConnectionOptions,
    credentials: z.object({
      host: z.string().describe("Mysql host"),
      port: z.number().default(3306).describe("Mysql port"),
      database: z.string().describe("Mysql database name"),
      username: z.string().describe("Mysql username"),
      password: z.string().describe("Mysql password"),
      parameters: z
        .object({ tls: z.enum(["true", "false", "skip-verify", "preferred"]) })
        .catchall(z.string().default(""))
        .optional()
        .default({ tls: "preferred" })
        .describe("Additional Mysql connection parameters"),
    }),
    credentialsUi: {
      password: {
        password: true,
      },
    },
    description: "MySQL is a popular open source object-relational database system.",
  },
  {
    id: "s3",
    usesBulker: true,
    icon: s3Icon,
    connectionOptions: BaseBulkerConnectionOptions.describe(
      JSON.stringify({
        limitations: {
          streamModeDisabled: "S3 destination doesn't support stream mode.",
          identityStitchingDisabled: "S3 destination doesn't support identityStitching.",
        },
      })
    ),
    title: "Amazon S3",
    tags: "Block Storage",
    description: "S3 is a cloud file storage service by Amazon",
    credentials: z
      .object({
        authenticationMethod: z
          .enum(["iam", "accessKey"])
          .optional()
          .default("accessKey")
          .describe(
            "Authentication Method::S3 authentication method: <a target='_blank' rel='noopener noreferrer' href='https://docs.jitsu.com/destinations/block-storage/s3#advanced-iam-role-for-jitsu'>IAM Role based</a> or Access Key"
          ),
        region: z.enum(s3Regions).default(s3Regions[0]).describe("S3 Region::S3 Region"),
        roleARN: z
          .string()
          .optional()
          .describe(
            "Role ARN::IAM role ARN. <a target='_blank' rel='noopener noreferrer' href='https://docs.jitsu.com/destinations/block-storage/s3#advanced-iam-role-for-jitsu'>How to create</a>"
          ),
        externalID: z.string().optional().describe("External ID::IAM external ID"),
        accessKeyId: z
          .string()
          .optional()
          .describe(
            "S3 Access Key Id::S3 Access Key Id. <a target='_blank' rel='noreferrer noopener' href='https://docs.aws.amazon.com/IAM/latest/UserGuide/security-creds.html#access-keys-and-secret-access-keys'>Create access key</a>"
          ),
        secretAccessKey: z.string().optional().describe("S3 Secret Access Key::S3 Secret Access Key"),
        bucket: z.string().describe("S3 Bucket Name::S3 Bucket Name"),
        endpoint: z.string().optional().describe("Custom endpoint of S3-compatible server"),
      })
      .merge(blockStorageSettings),
    credentialsUi: {
      authenticationMethod: {
        correction: obj => obj.authenticationMethod || "accessKey",
      },
      secretAccessKey: {
        hidden: obj => obj.authenticationMethod === "iam",
        password: true,
      },
      accessKeyId: {
        hidden: obj => obj.authenticationMethod === "iam",
      },
      endpoint: {
        hidden: obj => obj.authenticationMethod === "iam",
      },
      roleARN: {
        hidden: obj => obj.authenticationMethod !== "iam",
      },
      externalID: {
        // constants are not yet refreshed on form state change. so it is unconditional here
        constant: obj => obj.workspaceId,
      },
    },
  },
  {
    id: "gcs",
    usesBulker: true,
    icon: gcsIcon,
    connectionOptions: BaseBulkerConnectionOptions.describe(
      JSON.stringify({
        limitations: {
          streamModeDisabled: "Google Cloud Storage destination doesn't support stream mode.",
          identityStitchingDisabled: "Google Cloud Storage destination doesn't support identityStitching.",
        },
      })
    ),
    title: "Google Cloud Storage",
    tags: "Block Storage",
    credentials: z
      .object({
        accessKey: z.string().describe("Google Access Key::Google Access Key"),
        bucket: z.string().describe("GCS Bucket Name::GCS Bucket Name"),
      })
      .merge(blockStorageSettings),
    description: "Google Cloud Storage is a cloud file storage service by Google",
  },
  {
    id: "mixpanel",
    icon: mixpanelIcon,
    title: "Mixpanel",
    hybrid: true,
    tags: "Product Analytics",
    connectionOptions: CloudDestinationsConnectionOptions.merge(BatchModeOptions).merge(
      z.object({
        batchSize: z.number().min(1).max(2000).default(1000),
        batchSizeBytes: z.number().min(1).max(10_000_000).default(9_000_000),
        mode: z.enum(["stream", "batch"]).default("stream"),
      })
    ),
    credentials: meta.MixpanelCredentials,
    credentialsUi: meta.MixpanelCredentialsUi,
    description: "Mixpanel is a product analytics platform that provides insights into user behavior.",
    syncs: {
      "airbyte/source-google-ads": {
        syncOptions: z.object({}),
        description: (
          <>
            Jitsu exports ad spend data to from Google Ads to Mixpanel to measure return on ad spend (ROAS). Learn more
            about Mixpanel ROAS tracking{" "}
            <a
              href="https://mixpanel.com/blog/the-next-evolution-of-marketing-analytics/"
              target="_blank"
              rel="noreferrer noopener"
            >
              here
            </a>
          </>
        ),
      },
      "airbyte/source-facebook-marketing": {
        syncOptions: z.object({}),
        description: (
          <>
            Jitsu exports ad spend data to from Facebook to Mixpanel to measure return on ad spend (ROAS). Learn more
            about Mixpanel ROAS tracking{" "}
            <a
              href="https://mixpanel.com/blog/the-next-evolution-of-marketing-analytics/"
              target="_blank"
              rel="noreferrer noopener"
            >
              here
            </a>
          </>
        ),
      },
    },
  },
  {
    id: "intercom",
    icon: intercomIcon,
    title: "Intercom",
    tags: "Product Analytics",
    credentials: meta.IntercomDestinationCredentials,
    credentialsUi: meta.IntercomDestinationCredentialsUi,
    connectionOptions: CloudDestinationsConnectionOptions,
    description: (
      <>
        Jitsu updates intercom companies and users on each <code>.group()</code> and <code>.identify()</code> calls. For
        other events Jitsu sends them as custom events to Intercom
      </>
    ),
  },
  {
    id: "facebook-conversions",
    icon: facebookIcon,
    title: "Facebook Conversions API",
    tags: "Product Analytics",
    connectionOptions: CloudDestinationsConnectionOptions,
    credentials: meta.FacebookConversionApiCredentials,
    credentialsUi: meta.FacebookConversionApiCredentialsUi,
    description: "Facebook Conversion API is a tool for sending events to Facebook Ads Manager.",
  },
  {
    id: "june",
    icon: juneIcon,
    title: "June.so",
    tags: "Product Analytics",
    connectionOptions: CloudDestinationsConnectionOptions,
    credentials: meta.JuneCredentials,
    credentialsUi: meta.JuneCredentialsUi,
    description: "June.so is a product analytics platform that provides insights into user behavior.",
  },
  {
    id: "braze",
    icon: brazeIcon,
    title: "Braze",
    tags: "Product Analytics",
    connectionOptions: CloudDestinationsConnectionOptions,
    credentials: meta.BrazeCredentials,
    credentialsUi: meta.BrazeCredentialsUi,
    description: "Braze is a customer engagement platform used by businesses for multichannel marketing.",
  },
  {
    id: "heap",
    icon: heapIcon,
    title: "Heap",
    tags: "Product Analytics",
    connectionOptions: CloudDestinationsConnectionOptions,
    credentials: meta.HeapCredentials,
    description:
      "Heap (by Contentsquare) is a digital insights platform that automatically captures every user interaction. Send server-side events using the Heap HTTP API.",
  },
  {
    id: "openai-ads-conversions",
    connectionOptions: CloudDestinationsConnectionOptions,
    icon: openAiIcon,
    title: "OpenAI Ads Conversions API",
    tags: "Product Analytics",
    credentials: meta.OpenAiAdsConversionsCredentials,
    credentialsUi: meta.OpenAiAdsConversionsCredentialsUi,
    description:
      "Send server-side conversion events to OpenAI Ads (ChatGPT Ads) using the Conversions API, for measurement that is unaffected by ad blockers and browser tracking restrictions.",
  },
  {
    id: "nosto",
    connectionOptions: CloudDestinationsConnectionOptions,
    icon: nostoIcon,
    title: "Nosto",
    tags: "Personalization",
    credentials: meta.NostoCredentials,
    credentialsUi: meta.NostoCredentialsUi,
    description:
      "Send server-side events to Nosto's GraphQL API — session updates, cart contents, identity, orders and refunds. Use the same Nosto account as the Nosto Tag destination so both write to one visitor session.",
  },
  {
    id: "insider-one",
    icon: insiderOneIcon,
    title: "Insider One",
    tags: "Marketing Automation",
    connectionOptions: CloudDestinationsConnectionOptions,
    credentials: meta.InsiderOneCredentials,
    description:
      "Insider One is a cross-channel marketing platform for personalizing user experiences and triggering journeys. Send identify, track, and page events using the Insider One Upsert API.",
  },
  {
    id: "statsig",
    icon: statsigIcon,
    title: "Statsig",
    tags: "Product Analytics",
    connectionOptions: CloudDestinationsConnectionOptions,
    credentials: meta.StatsigCredentials,
    credentialsUi: meta.StatsigCredentialsUi,
    description:
      "Statsig is a feature flagging and experimentation platform. Send track, page, and identify events to Statsig's server-side log_event API to power experiment and feature-gate metrics. Feature gate evaluation stays in your storefront's Statsig SDK.",
  },
  {
    id: "amazon-conversions",
    icon: amazonIcon,
    title: "Amazon Conversions API",
    tags: "Attribution",
    connectionOptions: CloudDestinationsConnectionOptions,
    credentials: meta.AmazonConversionsCredentials,
    credentialsUi: meta.AmazonConversionsCredentialsUi,
    description:
      "Amazon Conversions API (Amazon Ads Events API) sends off-Amazon conversion events server-to-server so Amazon Ads can attribute and optimize against them. Match keys (email, phone, name, address) are normalized and SHA-256 hashed before they leave Chord.",
  },
  {
    id: "google-ads-conversions",
    icon: googleAdsIcon,
    title: "Google Ads Conversions",
    tags: "Attribution",
    connectionOptions: CloudDestinationsConnectionOptions,
    credentials: meta.GoogleAdsConversionsCredentials,
    credentialsUi: meta.GoogleAdsConversionsCredentialsUi,
    description:
      "Sends server-side conversions to Google Ads through Google's Data Manager API, alongside the Google Ads (Gtag) destination. Google merges the two on the order ID. Email, phone and name are normalized and SHA-256 hashed before they leave Chord.",
  },
  {
    id: "insider-pixel",
    isSynchronous: true,
    icon: insiderOneIcon,
    title: "Insider Pixel",
    tags: "Device Destinations",
    description:
      "The Insider Pixel loads the Insider Web SDK (ins.js) in the browser and sends page views, ecommerce events, and user profile updates directly from the storefront.",
    credentials: meta.InsiderPixelCredentials,
    deviceOptions: {
      type: "internal-plugin",
      name: "insider-pixel",
    } as DeviceOptions,
    connectionOptions: DeviceDestinationsConnectionOptions,
  },
  {
    id: "salesforce",
    icon: salesforceIcon,
    title: "Salesforce",
    tags: "Product Analytics",
    connectionOptions: CloudDestinationsConnectionOptions,
    credentials: meta.SalesforceCredentials,
    credentialsUi: {
      authorized: {
        hidden: true,
      },
      oauthIntegrationId: {
        hidden: true,
      },
      oauthConnectionId: {
        hidden: true,
      },
    },
    description: "Salesforce is the world's leading customer relationship management technology.",
  },
  {
    id: "mongodb",
    icon: mongodbIcon,
    title: "MongoDB",
    tags: "Data Warehouse",
    connectionOptions: CloudDestinationsConnectionOptions,
    credentials: meta.MongodbDestinationConfig,
    credentialsUi: meta.MongodbDestinationConfigUi,
    description:
      "MongoDB is a cross-platform NoSQL document-oriented database. Jitsu supports both self-hosted Mongo and MongoDB Atlas.",
  },
  {
    id: "ga4",
    icon: ga4Icon,
    title: "Google Analytics 4 (GA4 Measurement Protocol)",
    tags: "Product Analytics",
    connectionOptions: CloudDestinationsConnectionOptions,
    credentials: meta.Ga4Credentials,
    credentialsUi: meta.Ga4CredentialsUi,
    description:
      "Google Analytics 4 is a service offered by Google that reports website traffic data and marketing trends.",
  },
  {
    id: "posthog",
    icon: posthogIcon,
    title: "Posthog",
    tags: "Product Analytics",
    connectionOptions: CloudDestinationsConnectionOptions,
    credentials: meta.PosthogDestinationConfig,
    credentialsUi: meta.PosthogDestinationConfigUi,
    description:
      "Posthog is an open-source product analytics tool. Jitsu supports both self-hosted Posthog and Posthog Cloud.",
  },
  {
    id: "amplitude-cloud",
    icon: amplitudeIcon,
    connectionOptions: CloudDestinationsConnectionOptions,
    title: "Amplitude (Cloud Mode)",
    tags: "Product Analytics",
    credentials: meta.AmplitudeActionsCredentials,
    credentialsUi: meta.AmplitudeActionsCredentialsUi,
    description:
      "Amplitude is a product analytics platform. Supports all Amplitude actions including event tracking, user identification, revenue tracking, and group analytics.",
    documentation: (
      <>
        <p>
          This integration uses the Amplitude HTTP API v2 to send events directly to Amplitude without any third-party
          dependencies.
        </p>

        <h4>Default Event Routing</h4>
        <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "16px" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
              <th style={{ textAlign: "left", padding: "8px" }}>Source Event</th>
              <th style={{ textAlign: "left", padding: "8px" }}>Amplitude Action</th>
              <th style={{ textAlign: "left", padding: "8px" }}>Notes</th>
            </tr>
          </thead>
          <tbody>
            <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
              <td style={{ padding: "8px" }}>
                <code>track</code>
              </td>
              <td style={{ padding: "8px" }}>logEventV2</td>
              <td style={{ padding: "8px" }}>All track events except Order Completed</td>
            </tr>
            <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
              <td style={{ padding: "8px" }}>
                <code>Order Completed</code>
              </td>
              <td style={{ padding: "8px" }}>logPurchase</td>
              <td style={{ padding: "8px" }}>Includes revenue tracking</td>
            </tr>
            <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
              <td style={{ padding: "8px" }}>
                <code>identify</code>
              </td>
              <td style={{ padding: "8px" }}>identifyUser</td>
              <td style={{ padding: "8px" }}>Updates user properties</td>
            </tr>
            <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
              <td style={{ padding: "8px" }}>
                <code>page</code> / <code>screen</code>
              </td>
              <td style={{ padding: "8px" }}>logEventV2</td>
              <td style={{ padding: "8px" }}>Sent as "Viewed [name]"</td>
            </tr>
            <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
              <td style={{ padding: "8px" }}>
                <code>group</code>
              </td>
              <td style={{ padding: "8px" }}>groupIdentifyUser</td>
              <td style={{ padding: "8px" }}>Requires Amplitude Accounts add-on</td>
            </tr>
            <tr>
              <td style={{ padding: "8px" }}>
                <code>alias</code>
              </td>
              <td style={{ padding: "8px" }}>mapUser</td>
              <td style={{ padding: "8px" }}>Merges user identities</td>
            </tr>
          </tbody>
        </table>

        <h4>Custom Action Mappings</h4>
        <p>
          Use <b>Custom Action Mappings</b> to customize how events are routed and transformed before being sent to
          Amplitude.
        </p>
        <ul>
          <li>
            <b>Route specific events</b> — Send custom events to different Amplitude actions (e.g., send{" "}
            <code>Subscription Started</code> to <code>logPurchase</code>)
          </li>
          <li>
            <b>Rename events</b> — Transform event names before sending (e.g., <code>Button Clicked</code> →{" "}
            <code>CTA Interaction</code>)
          </li>
          <li>
            <b>Map custom fields</b> — Map any property from your events to Amplitude fields using dot notation
          </li>
          <li>
            <b>Array wildcards</b> — Use <code>[*]</code> to map arrays (e.g., <code>products[*].sku</code>)
          </li>
        </ul>

        <h4>Property Path Examples</h4>
        <ul>
          <li>
            <code>properties.product_id</code> — Access a top-level property
          </li>
          <li>
            <code>properties.products[0].sku</code> — Access first item in an array
          </li>
          <li>
            <code>properties.products[*].sku</code> — Access all items in an array
          </li>
          <li>
            <code>context.campaign.source</code> — Access nested context fields
          </li>
          <li>
            <code>traits.email</code> — Access user traits (for identify events)
          </li>
        </ul>

        <h4>Match Priority</h4>
        <ol>
          <li>
            <b>Exact match</b> — Event name matches exactly (e.g., <code>Order Completed</code>)
          </li>
          <li>
            <b>Type match</b> — Event type matches (e.g., <code>track</code> matches all track events)
          </li>
          <li>
            <b>Wildcard</b> — <code>*</code> matches all events (lowest priority)
          </li>
        </ol>
      </>
    ),
  },
  {
    id: "hubspot",
    icon: hubspotIcon,
    connectionOptions: CloudDestinationsConnectionOptions,
    title: "Hubspot",
    tags: "CRM",
    credentials: meta.HubspotCredentials,
    credentialsUi: meta.HubspotCredentialsConfigUi,
    description: "Hubspot is a CRM. Jitsu sends data to Hubspot API and updates contacts and company records",
    documentation: (
      <>
        The integration performs several functions:
        <ul>
          <li>
            For each <code>.identify()</code> event, it either creates or updates a contact in the CRM. Jitsu utilizes a
            custom property named <code>jitsu_user_id</code>, which is automatically generated, as the unique identifier
            for the contact object. This identifier corresponds to the <code>.userId</code> property within the{" "}
            <code>identify</code> event.
          </li>
          <li>
            For each <code>.group()</code> event, it either creates or updates a company profile in the CRM. Here, Jitsu
            employs a custom property called <code>jitsu_group_id</code>, which is also automatically generated, to
            serve as the unique identifier for the company object. This identifier is derived from the{" "}
            <code>.groupId</code> property within the <code>group</code> event.
          </li>
          <li>
            If an event includes both <code>groupId</code> and <code>userId</code>, Jitsu will establish a linkage
            between the two identifiers, effectively associating the user with the company.
          </li>
          <li>
            When the <code>sendPageViews</code> feature is activated, Jitsu will forward <code>page</code> events, along
            with other related events, to HubSpot for any identified user. <b>Important:</b> To utilize this
            functionality, ensure that your HubSpot plan includes access to the{" "}
            <a href="https://developers.hubspot.com/apisbytier">Events API</a>.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "devnull",
    icon: devnullIcon,
    connectionOptions: CloudDestinationsConnectionOptions,
    title: "/dev/null",
    tags: "Special",
    credentials: z.object({}),
    description:
      "This destination does not send any data anywhere. However, you can connect a function to this destination",
  },
  {
    id: "segment-proxy",
    connectionOptions: CloudDestinationsConnectionOptions,
    icon: segmentIcon,
    title: "Segment",
    tags: "Special",
    credentials: meta.SegmentCredentials,
    credentialsUi: meta.SegmentCredentialsUi,
    description: (
      <>
        Forward events for to Segment-compatible endpoint. It's useful if you want to use {branding.productName} for
        sending data to DWH and leave your existing Segment configuration for other purposes
      </>
    ),
  },
  {
    id: "webhook",
    icon: webhookIcon,
    title: "Webhook",
    tags: "Special",
    hybrid: true,
    connectionOptions: CloudDestinationsConnectionOptions.merge(BatchModeOptions).merge(
      z.object({
        batchSize: z.number().min(1).max(1000).default(100),
        mode: z.enum(["stream", "batch"]).default("stream"),
      })
    ),
    credentials: meta.WebhookDestinationConfig,
    credentialsUi: {
      headers: {
        editor: "StringArrayEditor",
      },
      payload: {
        editor: "SnippedEditor",
        editorProps: { languages: ["json", "text"], height: "300", syntaxCheck: { json: false } },
        hidden: obj => !obj.customPayload,
      },
    },
    description:
      "Send data to any HTTP endpoint. You can use this destination to send data to Slack, Discord, or any other service that accepts HTTP requests. ",
  },
  {
    id: "cdp-loopback",
    icon: ChordIconBlack,
    title: "CDP Loopback",
    tags: "Special",
    hybrid: true,
    connectionOptions: CloudDestinationsConnectionOptions,
    credentials: meta.CdpLoopbackCredentials,
    credentialsUi: meta.CdpLoopbackCredentialsUi,
    description:
      "Re-inject events back into Jitsu for additional processing. Events are automatically routed to the correct endpoint based on event type (track, identify, page, etc.).",
  },
  {
    id: "klaviyo",
    connectionOptions: CloudDestinationsConnectionOptions,
    credentials: meta.KlaviyoCredentials,
    credentialsUi: meta.KlaviyoCredentialsConfigUi,
    icon: klaviyoIcon,
    title: "Klaviyo",
    tags: "Email/SMS Marketing",
    description: "Klaviyo is a leading email and SMS marketing platform.",
  },
  {
    id: "iterable",
    connectionOptions: CloudDestinationsConnectionOptions,
    credentials: meta.IterableCredentials,
    credentialsUi: meta.IterableCredentialsUi,
    icon: iterableIcon,
    title: "Iterable",
    tags: "Email/SMS Marketing",
    description:
      "Iterable is a cross-channel marketing platform that helps businesses deliver personalized experiences.",
  },
  {
    id: "attentive",
    connectionOptions: CloudDestinationsConnectionOptions,
    credentials: meta.AttentiveCredentials,
    credentialsUi: meta.AttentiveCredentialsConfigUi,
    icon: attentiveIcon,
    title: "Attentive",
    tags: "Email/SMS Marketing",
    description: "Attentive is a personalized mobile messaging platform for e-commerce and retail brands.",
  },
  {
    id: "tiktok-conversions",
    connectionOptions: CloudDestinationsConnectionOptions,
    icon: tikTokIcon,
    title: "TikTok Conversions",
    tags: "Product Analytics",
    credentials: meta.TikTokConversionsDestinationConfig,
    credentialsUi: meta.TikTokConversionsDestinationConfigUi,
    description: "Measure and optimize the performance of your TikTok Ads",
  },
  {
    id: "microsoft-capi",
    connectionOptions: CloudDestinationsConnectionOptions,
    icon: bingIcon,
    title: "Microsoft Conversions API",
    tags: "Product Analytics",
    credentials: meta.MicrosoftCapiCredentials,
    credentialsUi: meta.MicrosoftCapiCredentialsUi,
    description:
      "Send server-side conversion events to Microsoft Advertising using the Conversions API for enhanced measurement and optimization.",
  },
  {
    id: "reddit-conversions",
    connectionOptions: CloudDestinationsConnectionOptions,
    icon: redditIcon,
    title: "Reddit Conversions API",
    tags: "Product Analytics",
    credentials: meta.RedditConversionsCredentials,
    credentialsUi: meta.RedditConversionsCredentialsUi,
    description:
      "Send server-side conversion events to Reddit Ads using the Conversions API (v3) for measurement that survives ad blockers and browser tracking restrictions.",
  },
  {
    id: "dotdigital",
    connectionOptions: CloudDestinationsConnectionOptions,
    icon: dotdigitalIcon,
    title: "Dotdigital",
    tags: "Marketing Automation",
    credentials: meta.DotdigitalCredentials,
    credentialsUi: meta.DotdigitalCredentialsUi,
    description:
      "Send server-side events and contact updates to Dotdigital using the v3 API — contact data fields, address book membership, and events written into Insight data collections. Use the same Dotdigital account as the Dotdigital Tag destination.",
  },
  {
    id: "pinterest-conversions",
    connectionOptions: CloudDestinationsConnectionOptions,
    icon: pinterestIcon,
    title: "Pinterest Conversions",
    tags: "Product Analytics",
    credentials: meta.PinterestConversionsDestinationConfig,
    credentialsUi: meta.PinterestConversionsDestinationConfigUi,
    description:
      "Maximize your visibility into captured conversions and build a better understanding of your ad's effectiveness.",
  },
  {
    id: "snapchat-conversions",
    connectionOptions: CloudDestinationsConnectionOptions,
    icon: snapchatIcon,
    title: "Snapchat Conversions API",
    tags: "Product Analytics",
    credentials: meta.SnapConversionsDestinationConfig,
    credentialsUi: meta.SnapConversionsDestinationConfigUi,
    description: "Measure and optimize the performance of your Snapchat Ads",
  },
  {
    id: "rokt-conversions",
    connectionOptions: CloudDestinationsConnectionOptions,
    icon: roktIcon,
    title: "Rokt Events API",
    tags: "Attribution",
    credentials: meta.RoktCredentials,
    credentialsUi: meta.RoktCredentialsUi,
    description:
      "Send server-side conversions and page views to Rokt for attribution and retargeting audiences. Replaces Rokt's deprecated Conversions API.",
  },
  {
    id: "ninetailed-cloud",
    connectionOptions: CloudDestinationsConnectionOptions,
    icon: ninetailedIcon,
    title: "Ninetailed Cloud Destination",
    tags: "Personalization",
    credentials: meta.NinetailedCloudDestinationConfig,
    description: "Send events to Ninetailed for personalization and user profile management.",
  },
  {
    id: "impact-conversions",
    connectionOptions: CloudDestinationsConnectionOptions,
    icon: impactIcon,
    title: "Impact",
    tags: "Attribution",
    credentials: meta.ImpactCredentials,
    credentialsUi: meta.ImpactCredentialsUi,
    description: "Tracks Conversions and website activity for Impact.",
  },
  {
    id: "algolia",
    connectionOptions: CloudDestinationsConnectionOptions,
    credentials: meta.AlgoliaCredentials,
    credentialsUi: meta.AlgoliaCredentialsUi,
    icon: algoliaIcon,
    title: "Algolia",
    tags: "Product Analytics",
    description: "Algolia is a search and discovery API for developing powerful and relevant digital experiences.",
  },
  {
    id: "delighted",
    connectionOptions: CloudDestinationsConnectionOptions,
    icon: delightedIcon,
    title: "Delighted",
    tags: "Customer Experience",
    credentials: meta.DelightedCredentials,
    credentialsUi: meta.DelightedCredentialsUi,
    description: "Delighted is the self-serve experience management platform of choice for the world's top brands",
  },
  {
    id: "northbeam",
    connectionOptions: CloudDestinationsConnectionOptions,
    icon: northbeamIcon,
    title: "Northbeam",
    tags: "Attribution",
    credentials: meta.NorthbeamCredentials,
    credentialsUi: meta.NorthbeamCredentialsUi,
    description:
      "Northbeam is a marketing intelligence platform for e-commerce attribution and analytics. Sends Order Completed events to Northbeam's Orders API.",
  },
  {
    id: "emotive",
    connectionOptions: CloudDestinationsConnectionOptions,
    icon: emotiveIcon,
    title: "Emotive",
    tags: "Email/SMS Marketing",
    credentials: meta.EmotiveCredentials,
    credentialsUi: meta.EmotiveCredentialsUi,
    description:
      "Emotive is a conversational SMS marketing platform. Sends Order Completed events to Emotive's Orders API.",
  },
];

export const ConsentRequirementsSchema = z
  .array(z.string())
  .default([])
  .describe(
    "Required consent categories. Select the consent categories required for this destination. Events will only be sent if the user has consented to all selected categories."
  );

export const SendNullConsentEventsSchema = z
  .boolean()
  .default(true)
  .describe(
    "Send events with null consent. If disabled, events will be dropped if the consent object is not present on the event."
  );

export const coreDestinationsMap = coreDestinations.reduce((acc, destination) => {
  destination.credentials = destination.credentials.extend({
    consentRequirements: ConsentRequirementsSchema,
    sendNullConsentEvents: SendNullConsentEventsSchema,
  });

  acc[destination.id] = destination;
  return acc;
}, {} as Record<string, DestinationType<any>>);
